import { useEffect, useRef, memo } from 'react';

interface TradingViewWidgetProps {
  symbol?: string;
  interval?: string;
  theme?: 'light' | 'dark';
  style?: '1' | '2' | '3' | '8' | '9';
  locale?: string;
  timezone?: string;
  toolbar_bg?: string;
  enable_publishing?: boolean;
  withdateranges?: boolean;
  hide_side_toolbar?: boolean;
  allow_symbol_change?: boolean;
  details?: boolean;
  hotlist?: boolean;
  calendar?: boolean;
  studies?: string[];
  container_id?: string;
  autosize?: boolean;
  width?: string | number;
  height?: string | number;
  showTrades?: boolean;
  trades?: Array<{
    timestamp: number;
    type: 'buy' | 'sell';
    price: number;
    size?: number;
  }>;
}

const TradingViewWidget = memo(({
  symbol = 'BINANCE:BTCUSDT',
  interval = '60',
  theme = 'dark',
  style = '1',
  locale = 'pt',
  timezone = 'America/Sao_Paulo',
  toolbar_bg = '#f1f3f6',
  enable_publishing = false,
  withdateranges = true,
  hide_side_toolbar = false,
  allow_symbol_change = true,
  details = true,
  hotlist = true,
  calendar = false,
  studies = [],
  container_id = 'tradingview_widget',
  autosize = true,
  width = '100%',
  height = '500',
  showTrades = false,
  trades = []
}: TradingViewWidgetProps) => {
  const container = useRef<HTMLDivElement>(null);
  const scriptLoaded = useRef(false);

  useEffect(() => {
    if (!container.current) return;

    // Clear container
    container.current.innerHTML = '';

    // Create widget container
    const widgetContainer = document.createElement('div');
    widgetContainer.id = container_id;
    container.current.appendChild(widgetContainer);

    // Load TradingView script
    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.type = 'text/javascript';
    script.async = true;

    const config = {
      autosize: autosize,
      width: autosize ? undefined : width,
      height: autosize ? undefined : height,
      symbol: symbol,
      interval: interval,
      timezone: timezone,
      theme: theme,
      style: style,
      locale: locale,
      toolbar_bg: toolbar_bg,
      enable_publishing: enable_publishing,
      withdateranges: withdateranges,
      range: "12M",
      hide_side_toolbar: hide_side_toolbar,
      allow_symbol_change: allow_symbol_change,
      details: details,
      hotlist: hotlist,
      calendar: calendar,
      studies: studies,
      container_id: container_id
    };

    script.innerHTML = `
      {
        "autosize": ${config.autosize},
        ${!config.autosize ? `"width": ${JSON.stringify(config.width)},` : ''}
        ${!config.autosize ? `"height": ${JSON.stringify(config.height)},` : ''}
        "symbol": ${JSON.stringify(config.symbol)},
        "interval": ${JSON.stringify(config.interval)},
        "timezone": ${JSON.stringify(config.timezone)},
        "theme": ${JSON.stringify(config.theme)},
        "style": ${JSON.stringify(config.style)},
        "locale": ${JSON.stringify(config.locale)},
        "toolbar_bg": ${JSON.stringify(config.toolbar_bg)},
        "enable_publishing": ${config.enable_publishing},
        "withdateranges": ${config.withdateranges},
        "range": ${JSON.stringify(config.range)},
        "hide_side_toolbar": ${config.hide_side_toolbar},
        "allow_symbol_change": ${config.allow_symbol_change},
        "details": ${config.details},
        "hotlist": ${config.hotlist},
        "calendar": ${config.calendar},
        "studies": ${JSON.stringify(config.studies)},
        "container_id": ${JSON.stringify(config.container_id)}
      }
    `;

    container.current.appendChild(script);

    // Add trade markers if provided
    if (showTrades && trades.length > 0) {
      setTimeout(() => {
        addTradeMarkers(trades);
      }, 3000); // Wait for chart to load
    }

    return () => {
      if (container.current) {
        container.current.innerHTML = '';
      }
    };
  }, [
    symbol, interval, theme, style, locale, timezone, toolbar_bg,
    enable_publishing, withdateranges, hide_side_toolbar, allow_symbol_change,
    details, hotlist, calendar, studies, container_id, autosize, width, height,
    showTrades, trades
  ]);

  const addTradeMarkers = (tradeData: typeof trades) => {
    // This would require TradingView's charting library API
    // For now, we'll just log the trades
    console.log('Trade markers:', tradeData);
  };

  return (
    <div 
      ref={container}
      className="tradingview-widget-container w-full"
      style={{ height: autosize ? '500px' : height }}
    >
      <div className="tradingview-widget-container__widget w-full h-full"></div>
      <div className="tradingview-widget-copyright">
        <a 
          href="https://www.tradingview.com/"
          rel="noopener nofollow"
          target="_blank"
          className="text-xs text-muted-foreground"
        >
          Tracks tudo sobre TradingView
        </a>
      </div>
    </div>
  );
});

TradingViewWidget.displayName = 'TradingViewWidget';

export default TradingViewWidget;